import React, { useEffect, useState } from 'react';
import { Loader2, Star, Trash2, Edit3, Clock, CheckCircle2, Send, Plus, X } from 'lucide-react';
import { StarRating, StarPicker } from './StarRating';
import { Button } from './ui/button';
import { toast } from '../hooks/use-toast';
import api from '../lib/api';

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString('es-MX', { year: 'numeric', month: 'short', day: 'numeric' }); } catch { return ''; }
};

const MyReviewsSection = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openForm, setOpenForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [rating, setRating] = useState(5);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/reviews/me');
      setReviews(r.data);
    } catch {
      // ignore
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setRating(5);
    setTitle('');
    setComment('');
    setOpenForm(true);
  };

  const openEdit = (r) => {
    setEditing(r);
    setRating(r.rating);
    setTitle(r.title);
    setComment(r.comment);
    setOpenForm(true);
  };

  const submit = async () => {
    if (!title.trim() || title.trim().length < 2) { toast({ title: 'Título muy corto' }); return; }
    if (!comment.trim() || comment.trim().length < 5) { toast({ title: 'Comentario muy corto', description: 'Escribe al menos 5 caracteres' }); return; }
    setSaving(true);
    try {
      const body = { rating, title: title.trim(), comment: comment.trim() };
      if (editing) {
        await api.patch(`/reviews/${editing.id}`, body);
        toast({ title: 'Reseña actualizada', description: 'Volverá a revisión del admin.' });
      } else {
        await api.post('/reviews', body);
        toast({ title: '¡Gracias por tu reseña!', description: 'La publicaremos tras revisión.' });
      }
      setOpenForm(false);
      await load();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'No se pudo guardar' });
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('¿Eliminar esta reseña?')) return;
    try {
      await api.delete(`/reviews/${id}`);
      toast({ title: 'Reseña eliminada' });
      load();
    } catch (e) { toast({ title: 'Error', description: 'No se pudo eliminar' }); }
  };

  return (
    <section className="bg-neutral-950 border border-white/10 rounded-2xl p-5 space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-white font-bold flex items-center gap-2">
            <Star className="w-4 h-4 text-orange-400 fill-orange-400" /> Mis reseñas
          </h3>
          <p className="text-xs text-white/50 mt-1">Comparte tu experiencia. Se publicarán tras aprobación del equipo.</p>
        </div>
        {!openForm && (
          <Button onClick={openNew} data-testid="new-review-btn" className="bg-orange-500 hover:bg-orange-600 text-black font-bold">
            <Plus className="w-4 h-4 mr-1" /> Nueva reseña
          </Button>
        )}
      </div>

      {openForm && (
        <div className="rounded-2xl border border-orange-500/30 bg-gradient-to-br from-orange-500/5 to-transparent p-5 space-y-4" data-testid="review-form">
          <div className="flex items-center justify-between">
            <p className="text-white font-bold text-sm uppercase tracking-wider">
              {editing ? 'Editar reseña' : 'Cuéntanos tu experiencia'}
            </p>
            <button onClick={() => setOpenForm(false)} className="text-white/50 hover:text-white"><X className="w-4 h-4" /></button>
          </div>

          <div className="flex flex-col items-center bg-black/40 rounded-xl p-4 border border-white/10">
            <p className="text-[11px] uppercase tracking-wider font-bold text-white/50 mb-2">Tu calificación</p>
            <StarPicker value={rating} onChange={setRating} size={38} />
            <p className="text-orange-300 text-xs font-semibold mt-2">
              {['','Malo','Regular','Bueno','Muy bueno','¡Excelente!'][rating]}
            </p>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">Título</label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ej. Sabor increíble y entrega rápida"
              maxLength={80}
              data-testid="review-title-input"
              className="w-full h-11 rounded-lg bg-black/40 border border-white/10 text-white text-[14px] px-3.5 outline-none placeholder:text-white/25 focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/20 transition-all"
            />
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-wider font-bold text-white/50 mb-1.5">Comentario</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={4}
              maxLength={500}
              placeholder="¿Cómo fue tu experiencia? Sabores, atención, empaque, tiempos de entrega..."
              data-testid="review-comment-input"
              className="w-full rounded-lg bg-black/40 border border-white/10 text-white text-[14px] p-3 outline-none placeholder:text-white/25 focus:border-orange-500/60 focus:ring-2 focus:ring-orange-500/20 transition-all resize-none"
            />
            <p className="text-right text-[10px] text-white/40 mt-1">{comment.length}/500</p>
          </div>

          <Button onClick={submit} disabled={saving} data-testid="submit-review-btn" className="w-full h-11 rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><Send className="w-4 h-4 mr-2" /> {editing ? 'Guardar cambios' : 'Publicar reseña'}</>}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-white/60 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Cargando reseñas...</div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8 bg-white/5 border border-dashed border-white/15 rounded-xl">
          <Star className="w-10 h-10 mx-auto text-orange-400 mb-3" />
          <p className="text-white font-semibold">Aún no has dejado ninguna reseña</p>
          <p className="text-white/50 text-sm mt-1">Sé el primero en compartir tu experiencia.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {reviews.map((r) => (
            <div key={r.id} data-testid="my-review-item" className="rounded-xl border border-white/10 bg-black/30 p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <StarRating value={r.rating} size={15} />
                    <span className={`inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full ${
                      r.approved ? 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30' : 'bg-amber-500/15 text-amber-300 border border-amber-500/30'
                    }`}>
                      {r.approved ? <><CheckCircle2 className="w-3 h-3" /> Publicada</> : <><Clock className="w-3 h-3" /> En revisión</>}
                    </span>
                    <span className="text-[10px] text-white/40 uppercase tracking-wider">{formatDate(r.created_at)}</span>
                  </div>
                  <h4 className="mt-2 font-bold text-white truncate">{r.title}</h4>
                  <p className="text-sm text-white/70 mt-1 line-clamp-3">{r.comment}</p>
                </div>
                <div className="flex flex-col gap-1.5 shrink-0">
                  <button onClick={() => openEdit(r)} className="w-9 h-9 rounded-lg bg-white/5 hover:bg-white/10 text-white/70 hover:text-orange-300 flex items-center justify-center transition" title="Editar">
                    <Edit3 className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(r.id)} className="w-9 h-9 rounded-lg bg-white/5 hover:bg-rose-500/15 text-white/70 hover:text-rose-300 flex items-center justify-center transition" title="Eliminar">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default MyReviewsSection;
